import dayjs from "dayjs";

// Returns a copy of `obj` with `value` at `path` (array of keys). Undefined removes the key.
export const setIn = (obj, path, value) => {
    if (!path.length) return value;
    const [key, ...rest] = path;
    const base = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
    const next = setIn(base[key] ?? (typeof rest[0] === "number" ? [] : {}), rest, value);
    if (next === undefined && !Array.isArray(base)) delete base[key];
    else base[key] = next;
    return base;
};

export const moveItem = (list, from, to) => {
    if (to < 0 || to >= list.length) return list;
    const copy = [...list];
    const [item] = copy.splice(from, 1);
    copy.splice(to, 0, item);
    return copy;
};

export const formatDate = (value) => (value ? dayjs(value).format("DD MMM YYYY HH:mm") : "—");

const NUMERIC = /^\s*-?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?\s*$/;
const isBlank = (value) => value === undefined || value === null || value === "" || value === "—";
const asNumber = (value) => (typeof value === "number" ? value : typeof value === "string" && NUMERIC.test(value) ? Number(value) : NaN);

/**
 * AG Grid comparator for cells that may hold numbers as text (CSV values):
 * numbers sort numerically, other text naturally ("item2" before "item10"),
 * dates by time, and blanks always last in either direction.
 */
export const compareCells = (a, b, nodeA, nodeB, isDescending) => {
    const blankA = isBlank(a);
    const blankB = isBlank(b);
    if (blankA || blankB) {
        if (blankA && blankB) return 0;
        return (blankA ? 1 : -1) * (isDescending ? -1 : 1);
    }
    const numberA = a instanceof Date ? a.getTime() : asNumber(a);
    const numberB = b instanceof Date ? b.getTime() : asNumber(b);
    if (!Number.isNaN(numberA) && !Number.isNaN(numberB)) return numberA - numberB;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
};

// For grid columns that sort on a raw date and show it formatted.
export const toTime = (value) => (value ? new Date(value).getTime() : null);

export const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

export const formatDuration = (ms) => {
    if (!ms && ms !== 0) return "—";
    if (ms < 1000) return `${ms} ms`;
    return `${(ms / 1000).toFixed(1)} s`;
};

export const errorMessage = (error, fallback = "Something went wrong. Please try again.") =>
    error?.message || error?.data?.message || fallback;

// Short text for an expression, shown in tables.
export const describeExpression = (expr) => {
    if (expr === undefined || expr === null || expr === "") return "";
    if (typeof expr === "string") return expr;
    if (expr.path !== undefined) return Array.isArray(expr.path) ? expr.path.join(" → ") : expr.path;
    if (expr.const !== undefined) return `"${expr.const}"`;
    if (expr.template !== undefined) return expr.template;
    if (expr.ref !== undefined) return expr.ref;
    if (expr.all) return `all(${expr.all.map(describeExpression).join(", ")})`;
    if (expr.any) return `any(${expr.any.map(describeExpression).join(", ")})`;
    if (expr.fallback) return expr.fallback.map(describeExpression).join(" → ");
    if (expr.lookup) return `lookup ${expr.lookup.in}`;
    if (expr.source_lookup) return `from ${expr.source_lookup.source}`;
    if (expr.case) return "case …";
    return JSON.stringify(expr);
};

export const displayValue = (value) => {
    if (value === undefined || value === null) return "";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
};

export const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};
